import { useState, useEffect, useRef } from "react"
import { useParams, useNavigate, useSearchParams } from "react-router-dom"

import VideoGrid from "../components/VideoGrid"
import LoadingScreen from "../components/LoadingScreen"
import LiveKitService from "../services/livekitService"
import TranscriptService from "../services/TranscriptService"
import ScreenShareTooltip from "../components/ScreenShareTooltip"
import CallControls from "../components/interview-page/CallControls"
import MeetingInfoBar from "../components/interview-page/MeetingInfoBar"
import AIAssistantSidebar from "../components/interview-page/AIAssistantSidebar"
import MeetingInfoDialog from "../components/interview-page/MeetingInfoDialog"
import EndCallOverlay from "../components/interview-page/EndCallOverlay"
import SelfViewLabel from "../components/interview-page/SelfViewLabel"
import ActionButtons from "../components/interview-page/ActionButtons"

import NetworkStatusOverlay from "../components/NetworkStatusOverlay"
import InterviewNotes from "../components/interview-page/InterviewNotes"
import PictureInPictureOverlay from "../components/PictureInPictureOverlay"
import InterviewGuardStatus from "../components/InterviewGuardStatus"
import PreJoinScreen from "../components/PreJoinScreen"
import CandidateChatSidebar from "../components/interview-page/CandidateChatSidebar"
import SuggestionTriggerService from "../services/SuggestionTriggerService"
import ChatNotificationPopup from "../components/interview-page/ChatNotificationPopup"
import { collection, query, orderBy, onSnapshot } from "firebase/firestore"
import CompilerPage from "../pages/CompilerPage"
import { doc, getDoc } from "firebase/firestore"

import TabSwitchDetectionService from "../services/TabSwitchDetectionService"
import TabSwitchModal from "../components/TabSwitchModal"

import InterviewGuardService from "../services/InterviewGuardService"
import InterviewGuardAlertModal from "../components/InterviewGuardAlertModal"

import { db } from "../services/firebase"

const InterviewPage = () => {
  const { meetingId } = useParams()

  const navigate = useNavigate()
  const livekitService = useRef(null)
  const transcriptService = useRef(null)

  // State variables
  const [localStream, setLocalStream] = useState(new MediaStream())

  const [localCameraStream, setLocalCameraStream] = useState(new MediaStream())
  const [localScreenShareStream, setLocalScreenShareStream] = useState(
    new MediaStream(),
  )

  const [remoteStreams, setRemoteStreams] = useState({})
  const [isMuted, setIsMuted] = useState(true)
  const [isScreenSharing, setIsScreenSharing] = useState(false)
  const [isCameraOn, setIsCameraOn] = useState(false)
  const [currentMeetingId, setCurrentMeetingId] = useState("")
  const [isInitializing, setIsInitializing] = useState(true)

  const [hasJoinedCall, setHasJoinedCall] = useState(false)
  const [showScreenShareTip, setShowScreenShareTip] = useState(false)
  const [isEndingCall, setIsEndingCall] = useState(false)
  const [userId, setUserId] = useState("") // Store user ID for chat
  const [showMeetingInfo, setShowMeetingInfo] = useState(false)
  const [currentTime, setCurrentTime] = useState("")
  const [userRole, setUserRole] = useState(null)

  const [showNotes, setShowNotes] = useState(false)

  const [isTogglingCamera, setIsTogglingCamera] = useState(false)
  const lastToggleTimeRef = useRef(0)

  const [localCameraTrack, setLocalCameraTrack] = useState(null)
  const [localScreenTrack, setLocalScreenTrack] = useState(null)
  const [localCameraMetadata, setLocalCameraMetadata] = useState(null)

  const [localScreenMetadata, setLocalScreenMetadata] = useState(null)

  const [remoteCameraStreams, setRemoteCameraStreams] = useState({})
  const [remoteScreenStreams, setRemoteScreenStreams] = useState({})

  const [remoteLiveKitTracks, setRemoteLiveKitTracks] = useState({})
  const [remoteTrackMetadata, setRemoteTrackMetadata] = useState({})

  const [isJoiningCall, setIsJoiningCall] = useState(false)
  const [showPreJoinScreen, setShowPreJoinScreen] = useState(true)
  const [preJoinSettings, setPreJoinSettings] = useState(null)

  const [showAIAssistant, setShowAIAssistant] = useState(false)
  const [showCandidateChat, setShowCandidateChat] = useState(false)

  const [aiSuggestions, setAiSuggestions] = useState([])
  const suggestionTriggerServiceRef = useRef(null)

  const [autoOpenSuggestions, setAutoOpenSuggestions] = useState(true)
  const [lastSeenSuggestionId, setLastSeenSuggestionId] = useState(null)

  const [latestMessage, setLatestMessage] = useState(null)
  const [showChatNotification, setShowChatNotification] = useState(false)
  const [lastSeenMessageId, setLastSeenMessageId] = useState(null)
  const [chatMessages, setChatMessages] = useState([])

  const [isRecording, setIsRecording] = useState(false)
  const [currentEgressId, setCurrentEgressId] = useState(null)

  const [connectionState, setConnectionState] = useState("connected")
  const [networkOnline, setNetworkOnline] = useState(navigator.onLine)
  const [showNetworkOverlay, setShowNetworkOverlay] = useState(false)
  const [showCodeEditor, setShowCodeEditor] = useState(false)

  const cheatingDetectionRef = useRef(null)
  const [showTabSwitchModal, setShowTabSwitchModal] = useState(false)

  const interviewGuardRef = useRef(null)
  const [showGuardAlert, setShowGuardAlert] = useState(false)
  const [guardAlertMessage, setGuardAlertMessage] = useState("")
  const [guardStatus, setGuardStatus] = useState("idle")

  const [orgId, setOrgId] = useState(null) 

  const [searchParams] = useSearchParams()

  useEffect(() => {
    if (aiSuggestions.length === 0 || !autoOpenSuggestions) return

    const latestSuggestion = aiSuggestions[aiSuggestions.length - 1]

    // Only auto-open if this is a new suggestion we haven't seen before
    if (latestSuggestion && latestSuggestion.id !== lastSeenSuggestionId) {
      console.log(
        "Auto-opening AI assistant for new suggestion:",
        latestSuggestion.id,
      )
      setShowAIAssistant(true)
      setShowNotes(false) // ADD THIS - Close notes if open
      setShowCandidateChat(false) // ADD THIS - Close candidate chat if open
      setLastSeenSuggestionId(latestSuggestion.id)
    }
  }, [aiSuggestions, autoOpenSuggestions, lastSeenSuggestionId])

 useEffect(() => {
   const hostToken = searchParams.get("host")
   if (!hostToken) {
     setUserRole("candidate")
     return
   }

   const verify = async () => {
     const roomSnap = await getDoc(doc(db, "rooms", meetingId))
     const data = roomSnap.data()
     const storedToken = data?.settings?.hostToken
     setUserRole(storedToken === hostToken ? "interviewer" : "candidate")
     setOrgId(data?.organizationId ?? null) // grab it here
   }
   verify()
 }, [meetingId, searchParams])

  useEffect(() => {
    if (!hasJoinedCall || !currentMeetingId) return

    const service = new InterviewGuardService()

    if (userRole === "interviewer") {
      service
        .onVerified(() => setGuardStatus("active"))
        .onAlert((message) => {
          setGuardAlertMessage(message)
          setGuardStatus("alert")
          setShowGuardAlert(true)
        })
        .onClear(() => {
          setShowGuardAlert(false)
          setGuardStatus("clear")
        })
    }

    service.initialize(currentMeetingId, userRole)
    interviewGuardRef.current = service

    return () => {
      service.cleanup()
      interviewGuardRef.current = null
    }
  }, [hasJoinedCall, currentMeetingId, userRole])

  useEffect(() => {
    if (!hasJoinedCall || !currentMeetingId) return

    const service = new TabSwitchDetectionService()

    if (userRole === "interviewer") {
      service
        .onTabSwitch((isSwitched) => setShowTabSwitchModal(isSwitched))
        .initialize(currentMeetingId, userRole)
    } else {
      service.initialize(currentMeetingId, userRole)
    }

    cheatingDetectionRef.current = service

    return () => {
      service.cleanup()
      cheatingDetectionRef.current = null
    }
  }, [hasJoinedCall, currentMeetingId, userRole])

  // Consolidated message listener - handles both chat messages and notifications
  useEffect(() => {
    if (!currentMeetingId || !userId) return

    try {
      const messagesRef = collection(db, "rooms", currentMeetingId, "messages")
      const q = query(messagesRef, orderBy("timestamp", "asc"))

      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          const newMessages = []
          snapshot.forEach((doc) => {
            newMessages.push({
              id: doc.id,
              ...doc.data(),
            })
          })

          setChatMessages(newMessages)

          // Show notification for latest message if applicable
          if (newMessages.length > 0) {
            const latestMsg = newMessages[newMessages.length - 1]

            // Only show notification if:
            // 1. Message is from someone else
            // 2. Chat sidebar is closed
            // 3. This is a new message we haven't seen
            if (
              latestMsg.senderId !== userId &&
              !showCandidateChat &&
              latestMsg.id !== lastSeenMessageId
            ) {
              setLatestMessage(latestMsg)
              setShowChatNotification(true)

              // Auto-dismiss after 10 seconds
              setTimeout(() => {
                setShowChatNotification(false)
              }, 10000)
            }
          }
        },
        (error) => {
          console.error("Error listening for chat messages:", error)
        },
      )

      return () => unsubscribe()
    } catch (error) {
      console.error("Error setting up chat message listener:", error)
    }
  }, [currentMeetingId, userId, showCandidateChat, lastSeenMessageId])

  useEffect(() => {
    if (!hasJoinedCall || !currentMeetingId || userRole !== "interviewer") {
      return
    }

    console.log("Initializing suggestion trigger service")

    const triggerService = new SuggestionTriggerService()
    triggerService.initialize(currentMeetingId)

    triggerService.onSuggestionReceived = (suggestion) => {
      console.log("Suggestion received:", suggestion)
      setAiSuggestions((prev) => [...prev, suggestion])
    }

    suggestionTriggerServiceRef.current = triggerService

    return () => {
      triggerService.cleanup()
    }
  }, [hasJoinedCall, currentMeetingId, userRole])

  // Update time every minute
  useEffect(() => {
    const updateTime = () => {
      const now = new Date()
      const hours = now.getHours()
      const minutes = now.getMinutes()
      const period = hours >= 12 ? "PM" : "AM"
      const formattedHours = hours % 12 || 12
      const formattedMinutes = minutes.toString().padStart(2, "0")
      setCurrentTime(`${formattedHours}:${formattedMinutes} ${period}`)
    }

    updateTime()
    const interval = setInterval(updateTime, 60000)
    return () => clearInterval(interval)
  }, [])

  // Initialize LiveKit service and TranscriptService
  useEffect(() => {
    console.log("Initializing LiveKit service and TranscriptService")

    const handleBeforeUnload = () => {
      console.log("Page is being unloaded, storing user state")
      // Make sure we persist any necessary session data
      if (livekitService.current && livekitService.current.room) {
        sessionStorage.setItem("lastMeetingId", meetingId)
      }
    }

    window.addEventListener("beforeunload", handleBeforeUnload)

    livekitService.current = new LiveKitService()

    // Initialize TranscriptService
    transcriptService.current = new TranscriptService()

    // Store temporary user ID for chat (will be updated on room join)
    setUserId(`temp-user-${Date.now()}`)

    livekitService.current.onParticipantConnected(
      (participantId, placeholderStream, metadata) => {
        console.log("Participant joined:", participantId, metadata)
        // Immediately add participant to remote streams, even without tracks
        if (metadata?.hasJoined) {
          console.log("Adding participant to UI immediately:", participantId)
          setRemoteStreams((prev) => ({
            ...prev,
            [participantId]: {
              stream: placeholderStream || new MediaStream(), // Empty stream initially
              isCameraOn: false,
              isScreenSharing: false,
              hasJoined: true, // Track that they've joined
            },
          }))
        }
      },
    )

    livekitService.current.onParticipantDisconnected((participantId) => {
      console.log("Participant left:", participantId)

      // Clean up LiveKit references
      setRemoteLiveKitTracks((prev) => {
        const updated = { ...prev }
        delete updated[participantId]
        return updated
      })

      setRemoteTrackMetadata((prev) => {
        const updated = { ...prev }
        delete updated[participantId]
        return updated
      })

      // Rest of existing onParticipantDisconnected logic...
      setRemoteStreams((prev) => {
        const newStreams = { ...prev }
        delete newStreams[participantId]
        return newStreams
      })
    })

    livekitService.current.onTrackSubscribed(
      (participantId, stream, metadata, livekitTrack) => {
        console.log(
          "Track received from:",
          participantId,
          "metadata:",
          metadata,
          "streamType:",
          metadata?.streamType,
        )

        // Store LiveKit track reference
        if (livekitTrack && metadata?.trackKind === "video") {
          setRemoteLiveKitTracks((prev) => ({
            ...prev,
            [participantId]: livekitTrack,
          }))
        }

        // Store track metadata
        if (metadata) {
          setRemoteTrackMetadata((prev) => ({
            ...prev,
            [participantId]: metadata,
          }))
        }

        // Handle updates (mute/unmute)
        if (metadata?.isUpdate) {
          setRemoteStreams((prev) => {
            const existing = prev[participantId] || {}

            if (metadata.trackKind === "video") {
              existing.isCameraOn = metadata.enabled && !metadata.muted
            }

            if (metadata.trackKind === "audio" && metadata.preserveStream) {
              existing.isAudioMuted = metadata.muted
            }

            return {
              ...prev,
              [participantId]: existing,
            }
          })
          return
        }

        // Handle new track additions - SEPARATE BY STREAM TYPE
        if (metadata?.hasTrack && stream) {
          const streamType = metadata.streamType // 'camera' or 'screen'

          if (streamType === "screen") {
            // Store screen share stream separately
            setRemoteScreenStreams((prev) => ({
              ...prev,
              [participantId]: stream,
            }))
            console.log(`Stored screen share stream for ${participantId}`)
          } else {
            // Store camera stream separately
            setRemoteCameraStreams((prev) => ({
              ...prev,
              [participantId]: stream,
            }))
            console.log(`Stored camera stream for ${participantId}`)
          }

          // Update metadata in remoteStreams
          setRemoteStreams((prev) => {
            const existing = prev[participantId] || {}

            const updated = {
              ...existing,
              stream: stream, // Keep for backward compatibility
              hasJoined: true,
              isCameraOn:
                streamType === "camera"
                  ? metadata.enabled && !metadata.muted
                  : existing.isCameraOn || false,
              isScreenSharing:
                streamType === "screen" || existing.isScreenSharing || false,
            }

            console.log("Updated remote participant", participantId, updated)

            return {
              ...prev,
              [participantId]: updated,
            }
          })
        }
      },
    )

    // Also update the onTrackUnsubscribed handler to only handle video tracks
    livekitService.current.onTrackUnsubscribed((participantId, trackKind) => {
      console.log("Track unsubscribed:", trackKind, "from:", participantId)

      if (trackKind === "video") {
        setRemoteLiveKitTracks((prev) => {
          const updated = { ...prev }
          delete updated[participantId]
          return updated
        })

        setRemoteTrackMetadata((prev) => {
          const updated = { ...prev }
          delete updated[participantId]
          return updated
        })
      }

      // Only handle video track removal, ignore audio track unsubscriptions
      if (trackKind === "video") {
        setRemoteStreams((prev) => {
          const existing = prev[participantId]
          if (!existing) return prev

          const updated = { ...existing }
          updated.isCameraOn = false
          updated.isScreenSharing = false
          // Keep the participant in the UI but remove video
          // Don't modify the stream for audio tracks

          return {
            ...prev,
            [participantId]: updated,
          }
        })
      }
      // For audio tracks, do nothing - let the stream persist
    })

    // Handle connection state changes
    livekitService.current.onConnectionState((state) => {
      console.log("LiveKit connection state:", state)
    })

    // Cleanup on unmount
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload)

      setLocalCameraTrack(null)
      setLocalScreenTrack(null)
      setRemoteLiveKitTracks({})
      setLocalCameraMetadata(null)
      setLocalScreenMetadata(null)
      setRemoteTrackMetadata({})

      if (livekitService.current) {
        livekitService.current.leaveRoom()
      }

      if (transcriptService.current) {
        transcriptService.current.cleanup()
      }
    }
  }, [meetingId])

  useEffect(() => {
    const initializeSession = async () => {
      if (!livekitService.current || !isInitializing || !preJoinSettings) return

      console.log("Proceeding with LiveKit session initialization")

      try {
        setIsInitializing(false)
        setIsJoiningCall(true)

        if (meetingId) {
          const participantName = preJoinSettings.name

          console.log(
            "Joining LiveKit room:",
            meetingId,
            "as:",
            participantName,
          )

          const result = await livekitService.current.joinRoom(
            meetingId,
            participantName,
          )
          const joinedRoomId = result.roomId

          setUserId(result.participantId)
          setCurrentMeetingId(joinedRoomId)

          console.log("Successfully joined LiveKit room:", joinedRoomId)

          // Apply pre-join settings
          setIsCameraOn(preJoinSettings.isCameraOn)
          setIsMuted(!preJoinSettings.isMicOn)

          // Enable microphone if user wanted it on
          if (preJoinSettings.isMicOn) {
            await livekitService.current.enableMicrophone(true)
          }

          // Enable camera if user wanted it on
          if (preJoinSettings.isCameraOn) {
            await livekitService.current.enableCamera(true)

            // CREATE SEPARATE CAMERA STREAM (same as toggleCamera does)
            const cameraTrack = livekitService.current.localTracks.get("camera")
            if (cameraTrack) {
              setLocalCameraTrack(cameraTrack)
              setLocalCameraMetadata({
                trackKind: "video",
                enabled: true,
                muted: false,
                isScreenSharing: false,
              })

              if (cameraTrack.mediaStreamTrack) {
                const cameraStream = new MediaStream([
                  cameraTrack.mediaStreamTrack,
                ])

                // Add audio track if available
                const micTrack =
                  livekitService.current.localTracks.get("microphone")
                if (micTrack && micTrack.mediaStreamTrack) {
                  cameraStream.addTrack(micTrack.mediaStreamTrack)
                }

                setLocalCameraStream(cameraStream)
                console.log(
                  "Created separate camera stream during initialization with tracks:",
                  cameraStream.getTracks().map((t) => t.kind),
                )
              }
            }
          }

          // Update local stream for UI (combined stream for backward compatibility)
          setLocalStream(
            livekitService.current.getLocalStream() || new MediaStream(),
          )

          // Initialize transcript service
          if (transcriptService.current) {
            transcriptService.current.setRoom(joinedRoomId)
            transcriptService.current.setSpeakerRole(userRole)
          }

          setHasJoinedCall(true)
          // ONLY hide PreJoinScreen after successful join
          setShowPreJoinScreen(false)
          console.log("LiveKit session initialization complete")
        } else {
          console.error("No meeting ID provided")
          alert(
            "No meeting ID provided. Please go back to the home page and try again.",
          )
          navigate("/")
        }
      } catch (error) {
        console.error("Failed to initialize LiveKit session:", error)
        alert(`Failed to join room: ${error.message}`)
        // Reset on error so user can try again
        setPreJoinSettings(null)
      } finally {
        setIsJoiningCall(false)
      }
    }

    initializeSession()
  }, [meetingId, navigate, isInitializing, preJoinSettings])

  // Network monitoring
  useEffect(() => {
    const handleOnline = () => {
      console.log("Browser: Network restored")
      setNetworkOnline(true)
      // Only hide overlay if LiveKit is also connected
      if (connectionState === "connected") {
        setShowNetworkOverlay(false)
      }
    }

    const handleOffline = () => {
      console.log("Browser: Network lost")
      setNetworkOnline(false)
      setShowNetworkOverlay(true)
    }

    window.addEventListener("online", handleOnline)
    window.addEventListener("offline", handleOffline)

    if (!navigator.onLine) {
      setShowNetworkOverlay(true)
    }

    return () => {
      window.removeEventListener("online", handleOnline)
      window.removeEventListener("offline", handleOffline)
    }
  }, [connectionState])

  useEffect(() => {
    if (!livekitService.current) return

    livekitService.current.onConnectionState((participantId, state) => {
      console.log("LiveKit connection state:", state)
      setConnectionState(state)

      // Show overlay for LiveKit-specific issues even if browser thinks we're online
      if (state === "reconnecting" || state === "disconnected") {
        setShowNetworkOverlay(true)
      } else if (state === "connected" && networkOnline) {
        setShowNetworkOverlay(false)
      }
    })
  }, [networkOnline])

  const handleJoinFromPreJoin = (settings) => {
    console.log("User joining with settings:", settings)
    setPreJoinSettings(settings)
  }

  const handleCancelPreJoin = () => {
    navigate("/")
  }

  const startRecording = async () => {
    if (!currentMeetingId) {
      alert("Meeting not initialized")
      return
    }

    try {
      console.log("Starting recording for room:", currentMeetingId)

      const response = await fetch(
        "https://hire100x-e1dd4.uc.r.appspot.com/recordings/start",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            roomName: currentMeetingId,
          }),
        },
      )

      const data = await response.json()

      if (data.success) {
        setIsRecording(true)
        setCurrentEgressId(data.egressId)
        console.log("Recording started:", data)
        // Optional: Show success message
        // You could add a toast notification here
      } else {
        throw new Error("Failed to start recording")
      }
    } catch (error) {
      console.error("Error starting recording:", error)
      alert("Failed to start recording. Please try again.")
    }
  }

  const stopRecording = async () => {
    if (!currentEgressId) {
      alert("No active recording found")
      return
    }

    try {
      console.log("Stopping recording:", currentEgressId)

      const response = await fetch(
        "https://hire100x-e1dd4.uc.r.appspot.com/recordings/stop",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            egressId: currentEgressId,
          }),
        },
      )

      const data = await response.json()

      if (data.success) {
        setIsRecording(false)
        setCurrentEgressId(null)
        console.log("Recording stopped:", data)
        // Optional: Show success message
        // You could add a toast notification here
      } else {
        throw new Error("Failed to stop recording")
      }
    } catch (error) {
      console.error("Error stopping recording:", error)
      alert("Failed to stop recording. Please try again.")
    }
  }

  const toggleCamera = async () => {
    if (!livekitService.current) {
      console.error("LiveKit service not initialized")
      return false
    }

    try {
      const now = Date.now()
      if (now - lastToggleTimeRef.current < 2000) {
        console.log("Camera toggle ignored - too soon after previous toggle")
        return false
      }

      setIsTogglingCamera(true)
      lastToggleTimeRef.current = now

      console.log(`Toggling camera: ${!isCameraOn}`)

      const enabled = await livekitService.current.enableCamera(!isCameraOn)
      setIsCameraOn(enabled)

      if (enabled) {
        // Store local LiveKit track reference FOR CAMERA
        const cameraTrack = livekitService.current.localTracks.get("camera")
        setLocalCameraTrack(cameraTrack)
        setLocalCameraMetadata({
          trackKind: "video",
          enabled: true,
          muted: false,
          isScreenSharing: false,
        })

        // CREATE SEPARATE CAMERA STREAM
        if (cameraTrack && cameraTrack.mediaStreamTrack) {
          const cameraStream = new MediaStream([cameraTrack.mediaStreamTrack])

          // Add audio track if available
          const micTrack = livekitService.current.localTracks.get("microphone")
          if (micTrack && micTrack.mediaStreamTrack) {
            cameraStream.addTrack(micTrack.mediaStreamTrack)
          }

          setLocalCameraStream(cameraStream)
          console.log(
            "Created separate camera stream with tracks:",
            cameraStream.getTracks().map((t) => t.kind),
          )
        }
      } else {
        setLocalCameraTrack(null)
        setLocalCameraMetadata(null)
        setLocalCameraStream(new MediaStream())
      }

      // Update combined local stream for UI (keeps backward compatibility)
      setLocalStream(livekitService.current.getLocalStream())

      console.log(`Camera ${enabled ? "enabled" : "disabled"}`)
      return enabled
    } catch (error) {
      console.error("Failed to toggle camera:", error)

      if (error.name === "NotAllowedError") {
        alert(
          "Camera access was denied. Please allow camera access in your browser settings.",
        )
      } else if (error.name === "NotFoundError") {
        alert("No camera detected. Please connect a camera and try again.")
      } else {
        alert(`Failed to toggle camera: ${error.message}`)
      }

      return false
    } finally {
      setIsTogglingCamera(false)
    }
  }

  const handleDismissGuardAlert = async () => {
    await interviewGuardRef.current?.acknowledgeAlert()
    setShowGuardAlert(false)
  }

  const handleDismissTabSwitchModal = async () => {
    await cheatingDetectionRef.current?.acknowledgeTabSwitch()
    setShowTabSwitchModal(false)
  }

  const startScreenShare = async () => {
    if (!livekitService.current) {
      console.error("LiveKit service not initialized")
      return false
    }

    try {
      console.log(`Toggling screen share: ${!isScreenSharing}`)

      const enabled =
        await livekitService.current.enableScreenShare(!isScreenSharing)
      setIsScreenSharing(enabled)

      if (enabled) {
        setShowScreenShareTip(true)

        // Store local LiveKit track reference FOR SCREEN SHARE
        const screenTrack =
          livekitService.current.localTracks.get("screenshare")
        setLocalScreenTrack(screenTrack)
        setLocalScreenMetadata({
          trackKind: "video",
          enabled: true,
          muted: false,
          isScreenSharing: true,
        })

        // CREATE SEPARATE SCREEN SHARE STREAM
        if (screenTrack && screenTrack.mediaStreamTrack) {
          const screenStream = new MediaStream([screenTrack.mediaStreamTrack])

          // Add audio track if available
          const micTrack = livekitService.current.localTracks.get("microphone")
          if (micTrack && micTrack.mediaStreamTrack) {
            screenStream.addTrack(micTrack.mediaStreamTrack)
          }

          setLocalScreenShareStream(screenStream)
          console.log(
            "Created separate screen share stream with tracks:",
            screenStream.getTracks().map((t) => t.kind),
          )
        }
      } else {
        setLocalScreenTrack(null)
        setLocalScreenMetadata(null)
        setLocalScreenShareStream(new MediaStream())
      }

      // Update combined local stream for UI (keeps backward compatibility)
      setLocalStream(livekitService.current.getLocalStream())

      console.log(`Screen share ${enabled ? "started" : "stopped"}`)
      return enabled
    } catch (error) {
      console.error("Failed to toggle screen share:", error)

      if (error.name === "NotAllowedError") {
        alert("Screen sharing was cancelled or denied.")
      } else {
        alert(`Screen sharing failed: ${error.message}`)
      }

      return false
    }
  }

  // REPLACE the toggleMute function:
  const toggleMute = async () => {
    if (!livekitService.current) {
      console.error("LiveKit service not initialized")
      return
    }

    try {
      console.log(`Toggling mute: ${isMuted}`)

      const enabled = await livekitService.current.enableMicrophone(isMuted)
      setIsMuted(!enabled)

      // IMPORTANT: Update local stream to reflect microphone state change
      const updatedStream = livekitService.current.getLocalStream()
      setLocalStream(updatedStream)
      console.log(
        `Local stream updated after mic toggle - audio tracks: ${
          updatedStream.getAudioTracks().length
        }`,
      )

      console.log(`Microphone ${enabled ? "enabled" : "disabled"}`)

      // Handle transcript service based on mute state
      if (transcriptService.current) {
        if (enabled) {
          const micTrack = livekitService.current.localTracks.get("microphone")
          const audioStream = micTrack?.mediaStreamTrack
            ? new MediaStream([micTrack.mediaStreamTrack])
            : null
          const started =
            await transcriptService.current.startListening(audioStream)
          if (started) {
            console.log("Transcription started successfully")
          } else {
            console.error("Failed to start transcription")
          }
        } else {
          console.log("Stopping speech recognition for transcription")
          transcriptService.current.stopListening()
        }
      }
    } catch (error) {
      console.error("Failed to toggle microphone:", error)
      alert(`Failed to toggle microphone: ${error.message}`)
    }
  }

  const endCall = async () => {
    try {
      setIsEndingCall(true)
      console.log("Ending call and cleaning up resources")

      if (suggestionTriggerServiceRef.current) {
        suggestionTriggerServiceRef.current.endSession()
        suggestionTriggerServiceRef.current.cleanup()
        suggestionTriggerServiceRef.current = null
      }

      // Clean up transcript service
      if (transcriptService.current) {
        console.log("Cleaning up transcription service")
        transcriptService.current.cleanup()
        transcriptService.current = null
      }

      // Clean up LiveKit service
      if (livekitService.current) {
        console.log("Leaving LiveKit room")
        await livekitService.current.leaveRoom()
        livekitService.current = null
      }

       if (interviewGuardRef.current) {
         await interviewGuardRef.current.endSession()
         interviewGuardRef.current.cleanup()
         interviewGuardRef.current = null
       }

      // Clear all state
      setLocalStream(null)
      setLocalCameraStream(new MediaStream()) // ADD THIS
      setLocalScreenShareStream(new MediaStream()) // ADD THIS
      setRemoteStreams({})
      setIsScreenSharing(false)
      setIsMuted(false)
      setIsCameraOn(false)
      console.log("Cleanup complete, navigating to home")
      navigate("/")
    } catch (error) {
      console.error("Error during call cleanup:", error)
      navigate("/")
    } finally {
      setIsEndingCall(false)
    }
  }

  const toggleAIAssistant = () => {
    setShowAIAssistant(!showAIAssistant)
    if (!showAIAssistant && showCandidateChat) {
      setShowCandidateChat(false) // Close candidate chat if opening AI assistant
    }
  }

  const toggleCandidateChat = () => {
    const newState = !showCandidateChat
    setShowCandidateChat(newState)

    if (newState) {
      // Opening chat - dismiss notification and mark as seen
      setShowChatNotification(false)
      if (latestMessage) {
        setLastSeenMessageId(latestMessage.id)
      }

      if (showAIAssistant) {
        setShowAIAssistant(false) // Close AI assistant if opening candidate chat
      }
    }
  }

  const handleChatNotificationClick = () => {
    setShowChatNotification(false)
    if (latestMessage) {
      setLastSeenMessageId(latestMessage.id)
    }
    setShowCandidateChat(true)
    if (showAIAssistant) {
      setShowAIAssistant(false)
    }
  }

  const navigateToCodeEditor = () => {
    // window.open(`/compiler?id=${meetingId}`, "_blank", "noopener,noreferrer")
    setShowCodeEditor(true)
  }

  // Toggle meeting info
  const toggleMeetingInfo = () => {
    setShowMeetingInfo(!showMeetingInfo)
  }

  if (showPreJoinScreen) {
    if (userRole === null) {
      return <LoadingScreen isLoading={true} />
    }
    return (
      <PreJoinScreen
        onJoinCall={handleJoinFromPreJoin}
        defaultName={""}
        onCancel={handleCancelPreJoin}
        isJoining={isJoiningCall}
        userRole={userRole}
      />
    )
  }

  // Replace the entire return statement with proper height constraints:
  return (
    <div className="fixed inset-0 bg-black text-white flex flex-col pl-2 pr-2 pt-2 pb-0">
      {/* Main content area with video and sidebar */}
      <div className="flex-1 flex overflow-hidden">
        {/* Video container - transitions from 100% to 70% width */}
        <div
          className={`relative transition-all duration-300 ease-in-out ${
            showAIAssistant || showCandidateChat ? "w-[85%]" : "w-full"
          } overflow-hidden`}
        >
          <div className="absolute inset-0 overflow-hidden">
            {hasJoinedCall ? (
              <VideoGrid
                localStream={localStream}
                remoteStreams={remoteStreams}
                isLocalScreenSharing={isScreenSharing}
                isCameraEnabled={isCameraOn}
                // Separate LiveKit track props
                localCameraTrack={localCameraTrack}
                localScreenTrack={localScreenTrack}
                remoteLiveKitTracks={remoteLiveKitTracks}
                localCameraMetadata={localCameraMetadata}
                localScreenMetadata={localScreenMetadata}
                remoteTrackMetadata={remoteTrackMetadata}
                // Separate streams for camera and screen share
                localCameraStream={localCameraStream}
                localScreenShareStream={localScreenShareStream}
                remoteCameraStreams={remoteCameraStreams}
                remoteScreenStreams={remoteScreenStreams}
              />
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  {!isEndingCall && (
                    <div className="text-xl mb-4">Loading...</div>
                  )}
                </div>
              </div>
            )}
{userRole === "interviewer" && (
  <div style={{ position: "absolute", top: 16, left: 16, zIndex: 50 }}>
    <InterviewGuardStatus
      status={guardStatus}
      alertMessage={guardAlertMessage}
    />
  </div>
)}

            {/* Self view label */}
            <SelfViewLabel />
          </div>
        </div>

        {/* ChatSidebar - slides in from right with 30% width */}
        <AIAssistantSidebar
          isOpen={showAIAssistant}
          onClose={toggleAIAssistant}
          meetingId={currentMeetingId}
          userId={userId}
          userRole={userRole}
          orgId={orgId}
          aiSuggestions={aiSuggestions}
          autoOpenSuggestions={autoOpenSuggestions}
          onToggleAutoOpen={() => setAutoOpenSuggestions(!autoOpenSuggestions)}
        />

        <CandidateChatSidebar
          isOpen={showCandidateChat}
          onClose={toggleCandidateChat}
          meetingId={currentMeetingId}
          userId={userId}
          messages={chatMessages}
        />
      </div>

      {/* Bottom controls bar - fixed height, always at bottom */}
      <div className="h-auto bg-black flex-shrink-0 border-gray-800">
        <div className="grid grid-cols-3 items-center py-3 px-4 gap-4">
          {/* Meeting info bar - left side */}
          <div className="hidden md:flex items-center justify-start">
            <MeetingInfoBar
              currentTime={currentTime}
              onToggleInfo={toggleMeetingInfo}
            />
          </div>

          {/* Empty div for mobile to maintain grid structure */}
          <div className="md:hidden"></div>

          {/* Main controls - always centered */}
          <div className="flex items-center justify-center">
            <CallControls
              isMuted={isMuted}
              isScreenSharing={isScreenSharing}
              isCameraOn={isCameraOn}
              isTogglingCamera={isTogglingCamera}
              toggleMute={toggleMute}
              toggleCamera={toggleCamera}
              startScreenShare={startScreenShare}
              endCall={endCall}
              disabled={showNetworkOverlay}
            />
          </div>

          {/* Right side actions */}
          <div className="hidden md:flex items-center justify-end">
            <ActionButtons
              toggleAIAssistant={toggleAIAssistant}
              toggleCandidateChat={toggleCandidateChat}
              toggleNotes={() => setShowNotes(!showNotes)}
              userRole={userRole}
              navigateToCodeEditor={navigateToCodeEditor}
              isRecording={isRecording}
              onStartRecording={startRecording}
              onStopRecording={stopRecording}
            />
          </div>

          {/* Empty div for mobile to maintain grid structure */}
          <div className="md:hidden"></div>
        </div>
      </div>
      {/* Meeting info dialog */}
      <MeetingInfoDialog isOpen={showMeetingInfo} onClose={toggleMeetingInfo} />
      {/* Screen sharing tooltip */}
      <ScreenShareTooltip
        isVisible={showScreenShareTip}
        onClose={() => setShowScreenShareTip(false)}
      />
      {/* Ending call overlay */}
      <EndCallOverlay isVisible={isEndingCall} />
      <InterviewNotes
        isOpen={showNotes}
        onClose={() => setShowNotes(false)}
        meetingId={currentMeetingId}
        userId={userId}
        userRole={userRole}
      />
      {showChatNotification && latestMessage && (
        <ChatNotificationPopup
          message={latestMessage.text}
          senderName={latestMessage.senderName || "Candidate"}
          onClick={handleChatNotificationClick}
          onDismiss={() => setShowChatNotification(false)}
        />
      )}
      <PictureInPictureOverlay
        isCameraOn={isCameraOn}
        isScreenSharing={isScreenSharing}
        localCameraTrack={localCameraTrack}
        localScreenTrack={localScreenTrack}
        remoteLiveKitTracks={remoteLiveKitTracks}
        localCameraMetadata={localCameraMetadata}
        localScreenMetadata={localScreenMetadata}
        remoteTrackMetadata={remoteTrackMetadata}
      />

      {showCodeEditor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div
            className="bg-white"
            style={{ width: "95vw", height: "95vh", overflow: "hidden" }}
          >
            <CompilerPage
              meetingId={currentMeetingId}
              onClose={() => setShowCodeEditor(false)}
            />
          </div>
        </div>
      )}

      <NetworkStatusOverlay isVisible={showNetworkOverlay} />

      {/* <TabSwitchModal
        isVisible={showTabSwitchModal}
        onDismiss={handleDismissTabSwitchModal}
      /> */}

      <InterviewGuardAlertModal
        isVisible={showGuardAlert}
        message={guardAlertMessage}
        onDismiss={handleDismissGuardAlert}
      />
    </div>
  )
}

export default InterviewPage
